import { Injectable, inject } from "@angular/core";

import { AuthService } from "../auth/auth.service";
import {
  RESOURCE_PERMISSION_LEVELS,
  ResourceAccessLevel,
  ResourcePermissionDefinition,
} from "../../shared/utils/business-permission-catalog";

const RESOURCE_BY_SECTION = new Map<string, ResourcePermissionDefinition>(
  RESOURCE_PERMISSION_LEVELS.map((resource) => [resource.section, resource]),
);

/** Sections declared in the permission catalog, used as the argument to every method here. */
export type AccessSection = string;

/**
 * Single answer to "may this user read this module" and "may this user change it".
 *
 * Pages used to spell out their own `hasRole(...)` lists, which drifted apart as View roles were
 * added: a page would gate its route on the View role but still render buttons that only a Manage
 * holder can use. Both questions are resolved here from the same catalog the permission editor
 * writes, so a new role only has to be declared once.
 */
@Injectable({ providedIn: "root" })
export class AccessPermissionService {
  private readonly auth = inject(AuthService);

  /** Manage implies view, so a legacy Manage-only user keeps the access they had. */
  canView(section: AccessSection): boolean {
    const resource = RESOURCE_BY_SECTION.get(section);
    if (!resource) return false;
    if (resource.view && this.auth.hasRole(resource.view)) return true;
    return this.canManage(section);
  }

  canManage(section: AccessSection): boolean {
    const resource = RESOURCE_BY_SECTION.get(section);
    if (!resource) return false;
    return resource.manage.some((role) => this.auth.hasRole(role));
  }

  /** True when the user may open the module but not change anything in it. */
  isViewOnly(section: AccessSection): boolean {
    return this.canView(section) && !this.canManage(section);
  }

  level(section: AccessSection): ResourceAccessLevel {
    if (this.canManage(section)) return "MANAGE";
    return this.canView(section) ? "VIEW" : "NONE";
  }

  canViewAny(sections: readonly AccessSection[]): boolean {
    return sections.some((section) => this.canView(section));
  }

  canManageAny(sections: readonly AccessSection[]): boolean {
    return sections.some((section) => this.canManage(section));
  }

  /**
   * Whether a single role is held. Kept here so components can express one-off checks
   * (a specific Manage role inside a module) without reaching for AuthService directly.
   */
  hasRole(role: string): boolean {
    return this.auth.hasRole(role);
  }
}
